import { StudentName } from "../../../shared-components/StudentName";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Avatar,
  CardContent,
  Card,
  Alert,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { FruitifyContainer } from "../../../shared-components/Containers";
import GeneralLoading from "../../../shared-components/Loading";
import { useApiClient } from "../../../api";
import { formatDistanceToNow } from "date-fns";
import {
  AssignmentQuestionAnswerViewer,
  StudentPerformanceSummaryStats,
} from "../components/AssignmentQuestionAnswerViewer";
import { useExportStudentFeedback } from "../hooks/Exporters";
import { withPageTracking } from "../../../services/postHog";
import { isHolisticMarking } from "../../../utils/preferences";
import { enqueueSnackbar } from "notistack";
import {
  hasProcessingAnswers,
  useStudentAssignmentAnswers,
} from "../hooks/useStudentAssignmentAnswers";

export const StudentSummaryAndQuickNav = ({
  student,
  lastSubmitted,
  studentView = false,
  assignmentId = null,
  nextStudentId = null,
  previousStudentId = null,
  onDownloadFeedback = () => {},
}) => {
  const navigate = useNavigate();

  const handleNavigate = (targetId) => {
    if (targetId) {
      navigate(`/assignments/answer/view/${assignmentId}/${targetId}`);
    }
  };
  return (
    <Card>
      <CardContent sx={{ p: 0.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ display: "flex", flexDirection: "column", flex: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar>{student.identifier[0]}</Avatar>
              <Box>
                <Stack direction={"row"} spacing={2} alignItems={"center"}>
                  <Typography variant="h5">
                    <StudentName student={student} />
                  </Typography>
                </Stack>
                {lastSubmitted && (
                  <Typography variant="body1" color="text.secondary">
                    Last Answer Submitted{" "}
                    {formatDistanceToNow(new Date(lastSubmitted), {
                      addSuffix: true,
                    })}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
          {!studentView && (
            <Button
              disabled={!onDownloadFeedback}
              variant={"outlined"}
              onClick={onDownloadFeedback}
            >
              Download feedback
            </Button>
          )}
        </Stack>
        {!studentView && (
          <Stack direction={"row"} spacing={2} sx={{ mt: 2 }}>
            <Button
              variant={"outlined"}
              disabled={!previousStudentId}
              onClick={() => handleNavigate(previousStudentId)}
              startIcon={<ChevronLeft />}
            >
              Previous Student
            </Button>
            <Button
              variant={"outlined"}
              disabled={!nextStudentId}
              onClick={() => handleNavigate(nextStudentId)}
              endIcon={<ChevronRight />}
            >
              Next Student
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const AnswerView = () => {
  const { assignmentId, studentId } = useParams();
  const ApiClient = useApiClient();
  const navigate = useNavigate();
  const { exportStudentFeedback } = useExportStudentFeedback();

  const [answer, setAnswer] = useState({});

  const {
    loading,
    error,
    allAssignmentInfo,
    mappedMarks,
    setMappedMarks,
    selectedAssignmentQuestion,
    setSelectedAssignmentQuestion,
    fetchStudentAnswers,
    pollDuringMarking,
  } = useStudentAssignmentAnswers(assignmentId, studentId);

  useEffect(() => {
    if (selectedAssignmentQuestion && allAssignmentInfo?.answers) {
      const answer = allAssignmentInfo?.answers.find(
        (ans) => ans.assignmentQuestion.id === selectedAssignmentQuestion.id,
      );
      setAnswer(answer || null);
    } else {
      setAnswer(null);
    }
  }, [selectedAssignmentQuestion, allAssignmentInfo?.answers]);

  useEffect(() => {
    if (assignmentId && studentId) {
      const loadAnswers = async () => {
        const result = await fetchStudentAnswers();
        if (hasProcessingAnswers(result?.answers)) pollDuringMarking();
      };
      loadAnswers();
    }
  }, [assignmentId, studentId]);

  const downloadFeedback = async () => {
    await exportStudentFeedback(
      allAssignmentInfo.assignment,
      allAssignmentInfo.student,
    );
  };

  if (error) {
    return <Alert>Unable to load data. {error}</Alert>;
  }

  if (loading || !allAssignmentInfo) {
    return <GeneralLoading />;
  }

  const handleHolisticMarkChange = async (newValue) => {
    try {
      // In your actual implementation, this sends the object to your Django JSONField
      const response = await ApiClient.assignment.updateHolisticMarkForAnswer({
        answer: answer.id,
        mark: newValue,
      });
      if (response.success && response.newAoMarks) {
        const parsedMarks = JSON.parse(response.newAoMarks);
        const updatedKeys = Object.keys(parsedMarks);
        // Flat update: find the object with these keys and swap it entirely
        setMappedMarks((prev) =>
          prev.map((marksObj) =>
            updatedKeys.some((key) => Object.hasOwn(marksObj, key))
              ? parsedMarks
              : marksObj,
          ),
        );
      } else {
        enqueueSnackbar("There was a problem changing the mark", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      enqueueSnackbar("There was a problem changing the mark", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const onMarkChange = (selectedAOId, newValue) => {
    setMappedMarks((prevMappedMarks) =>
      prevMappedMarks.map((marksObj) => {
        if (Object.prototype.hasOwnProperty.call(marksObj, selectedAOId)) {
          return {
            ...marksObj,
            [selectedAOId]: parseInt(newValue, 10),
          };
        }
        return marksObj;
      }),
    );
  };
  const totalMarks = mappedMarks.reduce((sum, marksObj) => {
    return (
      sum + Object.values(marksObj).reduce((objSum, mark) => objSum + mark, 0)
    );
  }, 0);

  const getAnswerMark = (answerId) => {
    const answerIndex = allAssignmentInfo.answers.findIndex(
      (ans) => ans.id === answerId,
    );
    if (answerIndex === -1) return 0;

    const marksObj = mappedMarks[answerIndex] ?? {};
    return Object.values(marksObj).reduce((sum, mark) => sum + mark, 0);
  };

  return (
    <FruitifyContainer>
      <Button
        variant={"card-action"}
        startIcon={<ChevronLeft />}
        sx={{ mb: 2, background: "transparent" }}
        onClick={() => navigate(`/assignments/view/${assignmentId}`)}
      >
        Back to assignment
      </Button>
      <StudentSummaryAndQuickNav
        lastSubmitted={allAssignmentInfo?.lastSubmitted} // TODO: have asked question of michael
        student={allAssignmentInfo.student}
        assignmentId={assignmentId}
        nextStudentId={allAssignmentInfo.nextStudentId}
        previousStudentId={allAssignmentInfo.previousStudentId}
        onDownloadFeedback={
          allAssignmentInfo.answers.length < 1 ? null : downloadFeedback
        }
      />
      <StudentPerformanceSummaryStats
        availableMarks={allAssignmentInfo.assignmentAvailableMarks}
        mark={totalMarks}
        noAnswers={allAssignmentInfo.answers.length === 0}
        lastAssignmentScore={allAssignmentInfo.lastAssignmentScore}
        markedHolistically={
          isHolisticMarking(allAssignmentInfo.assignment?.preferences?.data) &&
          allAssignmentInfo.assignment.assignmentQuestions.length === 1
        }
        onHolisticMarkChange={handleHolisticMarkChange}
      />
      <AssignmentQuestionAnswerViewer
        assignmentQuestions={allAssignmentInfo.assignment.assignmentQuestions}
        lastAssignmentScore={allAssignmentInfo.lastAssignmentScore}
        studentAnswers={allAssignmentInfo.answers}
        student={allAssignmentInfo.student}
        setStudentAnswer={setAnswer}
        studentAnswer={answer}
        setSelectedAssignmentQuestion={setSelectedAssignmentQuestion}
        selectedAssignmentQuestion={selectedAssignmentQuestion}
        mark={getAnswerMark(answer?.id)}
        onMarkChange={onMarkChange}
        onHolisticMarkChange={handleHolisticMarkChange}
        onSuccessfulSubmit={pollDuringMarking}
        assignmentPreferences={allAssignmentInfo.assignment?.preferences?.data}
        disableSubmit={allAssignmentInfo.assignment.disableSubmission}
      />
    </FruitifyContainer>
  );
};

export default withPageTracking(AnswerView, { feature: "answer-view" });
